import {
  GOOGLE_PLAY_STORE_LOCALES,
  STORE_LOCALE_SELECTION_OPTIONS,
} from "../utils/storeLocales";

export interface DeviceDimension {
  name: string;
  width: number;
  height: number;
  platform: "ios" | "android";
  category: string;
}

export interface TextOverlay {
  id: string;
  text: string;
  position: { x: number; y: number };
  fontSize: number;
  color: string;
  fontFamily: string;
  fontWeight?: number;
  textAlign?: "left" | "center" | "right";
}

export interface ScreenshotTemplate {
  templateId: string;
  appId: string;
  userId: string;
  name: string;
  baseImage: string; // URL or base64
  textOverlays: TextOverlay[];
  background?: string;
  deviceFrame?: string;
  dimension: DeviceDimension;
  createdAt: Date;
  updatedAt: Date;
}

export interface LocalizedScreenshot {
  screenshotId: string;
  templateId: string;
  appId: string;
  userId: string;
  locale: string;
  imageUrl: string;
  dimension: DeviceDimension;
  metadata: {
    generatedAt: Date;
    aiModel: string;
    translatedTexts: string[];
  };
}

export interface CreateTemplateDTO {
  appId: string;
  name: string;
  baseImage: string;
  textOverlays: TextOverlay[];
  background?: string;
  deviceFrame?: string;
  dimension: DeviceDimension;
}

export interface GenerateScreenshotDTO {
  templateId: string;
  targetLocales: string[];
}

export type ScreenshotTargetPlatform = "ios" | "android" | "both";
export type StoreBackgroundType = "color" | "pattern" | "gradient";
export type StoreBackgroundPattern =
  | "dot_grid"
  | "diagonal_lines"
  | "soft_waves"
  | "aurora_glow"
  | "diamond_weave";
export type StoreGradientPresetId =
  | "sunset_blush"
  | "hot_pink_glow"
  | "ocean_depth"
  | "mint_fresh"
  | "royal_plum"
  | "citrus_pop"
  | "midnight_steel"
  | "sky_lagoon"
  | "rose_gold"
  | "emerald_glow"
  | "twilight_berry"
  | "amber_sky"
  | "violet_dream"
  | "coral_reef"
  | "arctic_mist"
  | "lava_glow"
  | "forest_canopy"
  | "peach_bloom"
  | "neon_night"
  | "cobalt_sunrise"
  | "lavender_haze";
export type StoreFontStyle = "normal" | "italic";
export type StoreTextAlign = "left" | "center" | "right";
export type WaveStyleVariant = "gentle" | "balanced" | "dramatic";
export type StoreCompositionMaskType =
  | "rounded_rect"
  | "circle"
  | "squircle"
  | "arch_wave_top"
  | "arch_bottom"
  | "diamond"
  | "hexagon"
  | "shield"
  | "notched_rect";
export type StoreCompositionLayerKind =
  | "background"
  | "main_image"
  | "overlay_image"
  | "text_heading"
  | "text_subtitle";
export type AndroidDeviceFrameId =
  | "pixel_9_pro"
  | "galaxy_s25_ultra"
  | "oneplus_13"
  | "xiaomi_15_ultra";
export type StorePreviewDeviceId =
  | "iphone_15_pro_max"
  | "ipad_pro_12_9"
  | AndroidDeviceFrameId
  | "android_tablet_10";

export interface StoreDeviceFrameProfile {
  id: StorePreviewDeviceId;
  name: string;
  brand: string;
  platform: "ios" | "android";
  formFactor: "phone" | "tablet";
  previewDimension: DeviceDimension;
}

export interface StoreScreenshotTemplateStyle {
  backgroundType: StoreBackgroundType;
  backgroundColor: string;
  backgroundPattern: StoreBackgroundPattern;
  backgroundGradient: StoreGradientPresetId;
  patternColor: string;
  patternOpacity: number;
  patternScale: number;
  dotSizeScale: number;
  waveStyle: WaveStyleVariant;
  headingColor: string;
  subtitleColor: string;
  headingFontFamily: string;
  subtitleFontFamily: string;
  headingFontStyle: StoreFontStyle;
  subtitleFontStyle: StoreFontStyle;
  showSubtitle: boolean;
  headingWeight: number;
  subtitleWeight: number;
  headingSizeRatio: number;
  subtitleSizeRatio: number;
  headingXRatio: number;
  headingYRatio: number;
  subtitleXRatio: number;
  subtitleYRatio: number;
  headingMaxWidthRatio: number;
  subtitleMaxWidthRatio: number;
  headingTextAlign: StoreTextAlign;
  subtitleTextAlign: StoreTextAlign;
  useAbsoluteTextPlacement: boolean;
  showDeviceFrame: boolean;
  headingTopRatio: number;
  subtitleOffsetRatio: number;
  frameWidthRatio: number;
  frameHeightRatio: number;
  frameTopRatio: number;
  androidDeviceFrameId: AndroidDeviceFrameId;
  mainImageMask: {
    type: StoreCompositionMaskType;
    radiusRatio: number;
    insetRatio: number;
    waveDepthRatio: number;
  };
  mainImageTransform: {
    xRatio: number;
    yRatio: number;
    scale: number;
    rotationDeg: number;
    opacity: number;
  };
  layerStack: Array<{
    id: string;
    kind: StoreCompositionLayerKind;
    visible: boolean;
    zIndex: number;
    refId?: string;
  }>;
}

export type StoreTemplateLayoutId =
  | "simple_heading_device"
  | "feature_spotlight_device";
export type StoreScreenshotGenerationMode = "single_source" | "screen_slots";

export interface StoreScreenshotTemplateScreenSlot {
  slotId: string;
  order: number;
  name: string;
  enabled: boolean;
  style: StoreScreenshotTemplateStyle;
  defaultHeading?: string;
  defaultSubtitle?: string;
}

export interface StoreScreenshotTemplate {
  id: string;
  layout: StoreTemplateLayoutId;
  name: string;
  description: string;
  style: StoreScreenshotTemplateStyle;
  generationMode?: StoreScreenshotGenerationMode;
  screenSlots?: StoreScreenshotTemplateScreenSlot[];
  isCustom?: boolean;
  appId?: string;
  userId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GeneratedStoreScreenshot {
  id: string;
  appId: string;
  appName: string;
  platform: "ios" | "android";
  locale: string;
  dimension: DeviceDimension;
  sourceName: string;
  heading: string;
  subtitle: string;
  dataUrl: string;
  createdAt: string;
}

export interface StoreScreenshotGenerationVersion {
  versionId: string;
  appId: string;
  userId: string;
  versionNumber: number;
  templateId: string;
  templateName: string;
  createdAt: string;
  status?: "processing" | "completed" | "failed" | "partial";
  assetCount?: number;
  expectedAssetCount?: number;
  zipStoragePath?: string;
  zipDownloadUrl?: string;
  zipFileName?: string;
  zipCreatedAt?: string;
}

export interface StoredStoreScreenshotAsset {
  assetId: string;
  appId: string;
  userId: string;
  versionId: string;
  versionNumber: number;
  templateId: string;
  templateName: string;
  platform: "ios" | "android";
  locale: string;
  sourceName: string;
  heading: string;
  subtitle: string;
  dimension: DeviceDimension;
  downloadUrl: string;
  storagePath: string;
  createdAt: string;
}

export interface StoreScreenshotGenerationProgress {
  stage: "preparing" | "copy" | "render" | "saving" | "complete";
  message: string;
  completed: number;
  total: number;
  percent: number;
}

export interface StoreScreenshotZipExportProgress {
  stage: "queued" | "download" | "compress" | "upload" | "complete" | "failed";
  message: string;
  completed: number;
  total: number;
  percent: number;
}

export interface StoreScreenshotZipExportJob {
  jobId: string;
  appId: string;
  userId: string;
  versionId: string;
  status: "queued" | "processing" | "completed" | "failed";
  progress: StoreScreenshotZipExportProgress;
  downloadUrl?: string;
  fileName?: string;
  storagePath?: string;
  zipCreatedAt?: string;
  totalAssets?: number;
  error?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type GooglePlayScreenshotPublishMode =
  | "all_locales"
  | "single_locale"
  | "selected_locales";

export type GooglePlayScreenshotImageType =
  | "phoneScreenshots"
  | "sevenInchScreenshots"
  | "tenInchScreenshots"
  | string;

export type ScreenshotPublishDestination = "google_play" | "app_store_connect";

export interface GooglePlayScreenshotAssetGroupSummary {
  imageType: GooglePlayScreenshotImageType;
  label: string;
  assetCount: number;
  currentCount?: number;
  currentImages?: Array<{
    url: string;
    sha1?: string;
    sha256?: string;
  }>;
  proposedImages?: Array<{
    url: string;
    assetId?: string;
    sourceName?: string;
  }>;
}

export interface GooglePlayScreenshotValidationLocaleResult {
  locale: string;
  playLocale?: string;
  status: "success" | "missing" | "error" | "skipped";
  message: string;
  assetGroups: GooglePlayScreenshotAssetGroupSummary[];
}

export interface GooglePlayScreenshotValidationResponse {
  success: boolean;
  editId?: string;
  packageName: string;
  versionId: string;
  attemptedLocales: string[];
  results: GooglePlayScreenshotValidationLocaleResult[];
  message?: string;
  store?: "google_play" | "app_store_connect";
  appStoreConnectAppId?: string;
  appStoreVersionId?: string;
  appStoreVersionString?: string;
}

export interface GooglePlayScreenshotPublishLocaleResult {
  locale: string;
  playLocale?: string;
  status: "success" | "error" | "skipped";
  message: string;
  uploadedGroups?: GooglePlayScreenshotAssetGroupSummary[];
}

export interface GooglePlayScreenshotPublishBatchState {
  batchIndex: number;
  status: "queued" | "processing" | "completed" | "partial" | "failed";
  locales: string[];
  commitStatus?: "committed" | "not_committed";
  message?: string;
  updatedAt?: string;
}

export interface GooglePlayScreenshotPublishResponse {
  success: boolean;
  jobId?: string;
  status?: "queued" | "processing" | "completed" | "partial" | "failed";
  editId?: string;
  packageName: string;
  versionId: string;
  attemptedLocales: string[];
  totalLocales?: number;
  processedLocales?: number;
  totalBatches?: number;
  completedBatches?: number;
  results: GooglePlayScreenshotPublishLocaleResult[];
  batchStates?: GooglePlayScreenshotPublishBatchState[];
  commitStatus: "committed" | "not_committed";
  lastPublishedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  message?: string;
  store?: "google_play" | "app_store_connect";
  appStoreConnectAppId?: string;
  appStoreVersionId?: string;
  appStoreVersionString?: string;
}

export const STORE_SCREENSHOT_TEMPLATES: StoreScreenshotTemplate[] = [
  {
    id: "simple_heading_device",
    layout: "simple_heading_device",
    name: "Simple Heading + Device",
    description:
      "Minimal heading with centered device frame containing the uploaded screenshot.",
    style: {
      backgroundType: "color",
      backgroundColor: "#F6F8FC",
      backgroundPattern: "dot_grid",
      backgroundGradient: "sunset_blush",
      patternColor: "#667085",
      patternOpacity: 0.42,
      patternScale: 1,
      dotSizeScale: 1,
      waveStyle: "balanced",
      headingColor: "#101828",
      subtitleColor: "#475467",
      headingFontFamily: '"SF Pro Display", "Inter", "Segoe UI", sans-serif',
      subtitleFontFamily: '"SF Pro Text", "Inter", "Segoe UI", sans-serif',
      headingFontStyle: "normal",
      subtitleFontStyle: "normal",
      showSubtitle: true,
      headingWeight: 800,
      subtitleWeight: 500,
      headingSizeRatio: 0.058,
      subtitleSizeRatio: 0.03,
      headingXRatio: 0.5,
      headingYRatio: 0.065,
      subtitleXRatio: 0.5,
      subtitleYRatio: 0.135,
      headingMaxWidthRatio: 0.84,
      subtitleMaxWidthRatio: 0.76,
      headingTextAlign: "center",
      subtitleTextAlign: "center",
      useAbsoluteTextPlacement: false,
      showDeviceFrame: true,
      headingTopRatio: 0.065,
      subtitleOffsetRatio: 0.045,
      frameWidthRatio: 0.74,
      frameHeightRatio: 0.78,
      frameTopRatio: 0.16,
      androidDeviceFrameId: "pixel_9_pro",
      mainImageMask: {
        type: "rounded_rect",
        radiusRatio: 0.08,
        insetRatio: 0,
        waveDepthRatio: 0.1,
      },
      mainImageTransform: {
        xRatio: 0.5,
        yRatio: 0.5,
        scale: 1,
        rotationDeg: 0,
        opacity: 1,
      },
      layerStack: [],
    },
    generationMode: "single_source",
  },
  {
    id: "feature_spotlight_device",
    layout: "feature_spotlight_device",
    name: "Feature Spotlight",
    description:
      "Gradient hero with feature badge and premium right-aligned device presentation.",
    style: {
      backgroundType: "color",
      backgroundColor: "#0F172A",
      backgroundPattern: "diagonal_lines",
      backgroundGradient: "midnight_steel",
      patternColor: "#A5B4FC",
      patternOpacity: 0.4,
      patternScale: 1,
      dotSizeScale: 1,
      waveStyle: "balanced",
      headingColor: "#F8FAFC",
      subtitleColor: "#CBD5E1",
      headingFontFamily: '"SF Pro Display", "Inter", "Segoe UI", sans-serif',
      subtitleFontFamily: '"Inter", "Segoe UI", sans-serif',
      headingFontStyle: "normal",
      subtitleFontStyle: "normal",
      showSubtitle: true,
      headingWeight: 800,
      subtitleWeight: 500,
      headingSizeRatio: 0.053,
      subtitleSizeRatio: 0.027,
      headingXRatio: 0.2,
      headingYRatio: 0.12,
      subtitleXRatio: 0.2,
      subtitleYRatio: 0.19,
      headingMaxWidthRatio: 0.3,
      subtitleMaxWidthRatio: 0.26,
      headingTextAlign: "left",
      subtitleTextAlign: "left",
      useAbsoluteTextPlacement: false,
      showDeviceFrame: true,
      headingTopRatio: 0.12,
      subtitleOffsetRatio: 0.05,
      frameWidthRatio: 0.62,
      frameHeightRatio: 0.72,
      frameTopRatio: 0.22,
      androidDeviceFrameId: "pixel_9_pro",
      mainImageMask: {
        type: "rounded_rect",
        radiusRatio: 0.08,
        insetRatio: 0,
        waveDepthRatio: 0.1,
      },
      mainImageTransform: {
        xRatio: 0.5,
        yRatio: 0.5,
        scale: 1,
        rotationDeg: 0,
        opacity: 1,
      },
      layerStack: [],
    },
    generationMode: "single_source",
  },
];

export const DEFAULT_STORE_TEMPLATE_STYLE: StoreScreenshotTemplateStyle =
  STORE_SCREENSHOT_TEMPLATES[0].style;

export const STORE_DEVICE_FRAME_PROFILES: StoreDeviceFrameProfile[] = [
  {
    id: "iphone_15_pro_max",
    name: "iPhone 15 Pro Max",
    brand: "Apple",
    platform: "ios",
    formFactor: "phone",
    previewDimension: {
      name: "iPhone 15 Pro Max",
      width: 1290,
      height: 2796,
      platform: "ios",
      category: "iPhone",
    },
  },
  {
    id: "ipad_pro_12_9",
    name: 'iPad Pro 12.9"',
    brand: "Apple",
    platform: "ios",
    formFactor: "tablet",
    previewDimension: {
      name: 'iPad Pro 12.9"',
      width: 2048,
      height: 2732,
      platform: "ios",
      category: "iPad",
    },
  },
  {
    id: "pixel_9_pro",
    name: "Pixel 9 Pro",
    brand: "Google",
    platform: "android",
    formFactor: "phone",
    previewDimension: {
      name: "Pixel 9 Pro",
      width: 720,
      height: 1600,
      platform: "android",
      category: "Phone",
    },
  },
  {
    id: "galaxy_s25_ultra",
    name: "Galaxy S25 Ultra",
    brand: "Samsung",
    platform: "android",
    formFactor: "phone",
    previewDimension: {
      name: "Galaxy S25 Ultra",
      width: 720,
      height: 1560,
      platform: "android",
      category: "Phone",
    },
  },
  {
    id: "oneplus_13",
    name: "OnePlus 13",
    brand: "OnePlus",
    platform: "android",
    formFactor: "phone",
    previewDimension: {
      name: "OnePlus 13",
      width: 720,
      height: 1584,
      platform: "android",
      category: "Phone",
    },
  },
  {
    id: "xiaomi_15_ultra",
    name: "Xiaomi 15 Ultra",
    brand: "Xiaomi",
    platform: "android",
    formFactor: "phone",
    previewDimension: {
      name: "Xiaomi 15 Ultra",
      width: 720,
      height: 1600,
      platform: "android",
      category: "Phone",
    },
  },
  {
    id: "android_tablet_10",
    name: 'Android Tablet 10"',
    brand: "Android",
    platform: "android",
    formFactor: "tablet",
    previewDimension: {
      name: '10" Tablet',
      width: 1600,
      height: 2560,
      platform: "android",
      category: "Tablet",
    },
  },
];

// iOS Dimensions
export const iOS_DIMENSIONS: DeviceDimension[] = [
  {
    name: 'iPhone 6.9"',
    width: 1260,
    height: 2736,
    platform: "ios",
    category: "iPhone",
  },
  {
    name: 'iPhone 6.9"',
    width: 1290,
    height: 2796,
    platform: "ios",
    category: "iPhone",
  },
  {
    name: 'iPhone 6.9"',
    width: 1320,
    height: 2868,
    platform: "ios",
    category: "iPhone",
  },
  {
    name: 'iPhone 6.5"',
    width: 1284,
    height: 2778,
    platform: "ios",
    category: "iPhone",
  },
  {
    name: 'iPhone 6.5"',
    width: 1242,
    height: 2688,
    platform: "ios",
    category: "iPhone",
  },
  {
    name: 'iPhone 6.3"',
    width: 1179,
    height: 2556,
    platform: "ios",
    category: "iPhone",
  },
  {
    name: 'iPhone 6.3"',
    width: 1206,
    height: 2622,
    platform: "ios",
    category: "iPhone",
  },
  {
    name: 'iPhone 6.1"',
    width: 1170,
    height: 2532,
    platform: "ios",
    category: "iPhone",
  },
  {
    name: 'iPhone 6.1"',
    width: 1125,
    height: 2436,
    platform: "ios",
    category: "iPhone",
  },
  {
    name: 'iPhone 6.1"',
    width: 1080,
    height: 2340,
    platform: "ios",
    category: "iPhone",
  },
  {
    name: 'iPhone 5.5"',
    width: 1242,
    height: 2208,
    platform: "ios",
    category: "iPhone",
  },
  {
    name: 'iPhone 4.7"',
    width: 750,
    height: 1334,
    platform: "ios",
    category: "iPhone",
  },
  {
    name: 'iPad Pro 12.9"',
    width: 2048,
    height: 2732,
    platform: "ios",
    category: "iPad",
  },
];

export const GENERATOR_IOS_DIMENSIONS: DeviceDimension[] = [
  {
    name: 'iPhone 6.7"',
    width: 1290,
    height: 2796,
    platform: "ios",
    category: "iPhone",
  },
  {
    name: 'iPhone 6.5"',
    width: 1242,
    height: 2688,
    platform: "ios",
    category: "iPhone",
  },
  {
    name: 'iPhone 5.5"',
    width: 1242,
    height: 2208,
    platform: "ios",
    category: "iPhone",
  },
  {
    name: 'iPad Pro 12.9"',
    width: 2048,
    height: 2732,
    platform: "ios",
    category: "iPad",
  },
];

// Android Dimensions
export const ANDROID_DIMENSIONS: DeviceDimension[] = [
  {
    name: "Phone",
    width: 1080,
    height: 1920,
    platform: "android",
    category: "Phone",
  },
  {
    name: '7" Tablet',
    width: 1200,
    height: 1920,
    platform: "android",
    category: "Tablet",
  },
  {
    name: '10" Tablet',
    width: 1600,
    height: 2560,
    platform: "android",
    category: "Tablet",
  },
];

export const ALL_DIMENSIONS = [...iOS_DIMENSIONS, ...ANDROID_DIMENSIONS];
export const GENERATOR_DIMENSIONS = [
  ...GENERATOR_IOS_DIMENSIONS,
  ...ANDROID_DIMENSIONS,
];

// Supported locales
export const SUPPORTED_LOCALES = GOOGLE_PLAY_STORE_LOCALES.map((locale) => ({
  code: locale.code,
  name: locale.name,
}));

export const SCREENSHOT_LOCALES = STORE_LOCALE_SELECTION_OPTIONS.map(
  (locale) => ({
    code: locale.code,
    name: locale.name,
    flag: locale.flag,
    speakers: locale.speakers || "",
    supportsGooglePlay: locale.supportsGooglePlay,
    supportsAppStore: locale.supportsAppStore,
  }),
);
